<template>
    <div class="current-condition-section-modern">
        <div class="section-header-modern">
            <div class="header-title-modern">
                <i class="fas fa-stethoscope"></i>
                <h4>{{ apiData.title || 'Padecimiento Actual' }}</h4>
            </div>
        </div>

        <!-- Padecimiento Actual Principal -->
        <div v-if="!isCurrentConditionActive" class="add-fields-section">
            <button
class="add-field-btn" title="Agregar Descripción del Padecimiento Actual"
                @click="addCurrentCondition">
                <i class="fas fa-clipboard-medical"></i>
                <span>Descripción del Padecimiento Actual</span>
            </button>
        </div>

        <div v-if="isCurrentConditionActive" class="main-condition-modern">
            <div class="field-header">
                <label class="form-label-modern">
                    <span class="label-content-modern">
                        <i class="fas fa-clipboard-medical"></i>
                        <span>Descripción del Padecimiento Actual</span>
                    </span>
                </label>
                <button class="close-field-btn" title="Cerrar campo" @click="removeCurrentCondition">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="textarea-wrapper-modern">
                <textarea
:value="currentCondition" class="form-textarea-modern"
                    placeholder="Describe el padecimiento actual del paciente..." rows="4"
                    @input="updateCurrentCondition($event.target.value)"></textarea>
                <div class="textarea-footer-modern">
                    <div class="textarea-stats-modern">
                        <span class="stat-item-modern">
                            <i class="fas fa-font"></i>
                            {{ currentCondition.length }} caracteres
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Interrogatorio por Aparatos y Sistemas -->
        <div class="systems-section-modern">
            <div class="subsection-header-modern">
                <i class="fas fa-heartbeat"></i>
                <h5>Interrogatorio por Aparatos y Sistemas</h5>
            </div>

            <!-- Botones para agregar campos -->
            <div v-if="inactiveFields.length > 0" class="add-fields-section">
                <button
v-for="field in inactiveFields" :key="field.name" class="add-field-btn"
                    :title="'Agregar ' + field.label" @click="addField(field.name)">
                    <i :class="getSystemIcon(field.name)"></i>
                    <span>{{ field.label }}</span>
                </button>
            </div>

            <!-- Campos activos (con contenido o activados) -->
            <div v-if="activeFields.length > 0" class="fields-grid-compact">
                <div v-for="field in activeFields" :key="field.name" class="field-group-compact full-width">
                    <div class="field-header">
                        <label class="form-label-modern">
                            <span class="label-content-modern">
                                <i :class="getSystemIcon(field.name)"></i>
                                <span>{{ field.label }}</span>
                            </span>
                        </label>
                        <button
v-if="isFieldCloseable(field.name)" class="close-field-btn" title="Cerrar campo"
                            @click="removeField(field.name)">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    <div class="textarea-wrapper-modern">
                        <textarea
:value="field.model" class="form-textarea-modern"
                            :placeholder="getPlaceholder(field.name)" rows="3"
                            @input="updateFieldValue(field.name, $event.target.value)"></textarea>
                        <div class="textarea-footer-modern">
                            <div class="textarea-stats-modern">
                                <span class="stat-item-modern">
                                    <i class="fas fa-font"></i>
                                    {{ (field.model || '').length }} caracteres
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="last-updated-wrapper">
            <LastUpdated :updated-at="updated_at" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import api from '@/services/api';
import { saveToLocalStorage } from '@/store/Expedientes/pacientes';
import LastUpdated from "@/components/LastUpdated.vue";
import debounce from 'lodash.debounce';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    },
    data: {
        type: Object,
        default: () => ({})
    }
});

const apiData = {
    title: "Padecimiento Actual",
    data: [
        { name: 'skin_and_annexes', label: 'Piel y Anexos', isVisible: true, category: '1' },
        { name: 'digestive', label: 'Digestivo', isVisible: true, category: '1' },
        { name: 'neuropsychiatric', label: 'Neuropsiquiátrico', isVisible: true, category: '1' },
        { name: 'cardio_circulatory', label: 'Cardio Circulatorio', isVisible: true, category: '1' },
        { name: 'urinary', label: 'Urinario', isVisible: true, category: '1' },
        { name: 'genital_breast', label: 'Genital y mama', isVisible: true, category: '1' },
        { name: 'respiratory', label: 'Respiratorio', isVisible: true, category: '1' },
        { name: 'musculoskeletal', label: 'Músculo esquelético', isVisible: true, category: '1' }
    ]
};

const defaultFields = [
    { name: 'skin_and_annexes', label: 'Piel y anexos', isVisible: true, category: '1' },
    { name: 'digestive', label: 'Digestivo', isVisible: true, category: '1' },
    { name: 'neuropsychiatric', label: 'Neuropsiquiátrico', isVisible: true, category: '1' },
    { name: 'cardio_circulatory', label: 'Cardio Circulatorio', isVisible: true, category: '1' },
    { name: 'urinary', label: 'Urinario', isVisible: true, category: '1' },
    { name: 'genital_breast', label: 'Genital/mama', isVisible: true, category: '1' },
    { name: 'respiratory', label: 'Respiratorio', isVisible: true, category: '1' },
    { name: 'musculoskeletal', label: 'Músculo esquelético', isVisible: true, category: '1' }
];

const fields = ref((apiData.data && apiData.data.length > 0 ? apiData.data : defaultFields).map(field => ({
    ...field,
    model: '',
    isActive: false
})));

const currentCondition = ref('');
const isCurrentConditionActive = ref(false);

const addCurrentCondition = () => {
    isCurrentConditionActive.value = true;
};

const removeCurrentCondition = () => {
    isCurrentConditionActive.value = false;
};

const showOpen = ref(true);
const updated_at = ref('');

// Computed para campos activos
const activeFields = computed(() => {
    return fields.value.filter(field => field.isActive);
});

// Computed para campos inactivos
const inactiveFields = computed(() => {
    return fields.value.filter(field => !field.isActive && field.isVisible === showOpen.value);
});

// Función para agregar campo
const addField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) {
        field.isActive = true;
    }
};

// Función para remover campo
const removeField = (name) => {
    const field = fields.value.find(f => f.name === name);
    if (field) {
        field.isActive = false;
    }
};

const isFieldCloseable = (name) => {
    const field = fields.value.find(f => f.name === name);
    return field ? true : false;
};

const updateCurrentCondition = (value) => {
    currentCondition.value = value;
    debouncedSaveCurrentConditionData();
};

const updateFieldValue = (name, value) => {
    const field = fields.value.find(field => field.name === name);
    if (field) {
        field.model = value;
        debouncedSaveCurrentConditionData();
    }
};

// Iconos para cada sistema
const getSystemIcon = (name) => {
    const icons = {
        skin_and_annexes: 'fas fa-hand-sparkles',
        digestive: 'fas fa-stomach',
        neuropsychiatric: 'fas fa-brain',
        cardio_circulatory: 'fas fa-heart-pulse',
        urinary: 'fas fa-kidneys',
        genital_breast: 'fas fa-venus',
        respiratory: 'fas fa-lungs',
        musculoskeletal: 'fas fa-bone'
    };
    return icons[name] || 'fas fa-file-medical';
};

// Placeholders personalizados
const getPlaceholder = (name) => {
    const placeholders = {
        skin_and_annexes: 'Ej: Lesiones cutáneas, cambios en piel, cabello, uñas...',
        digestive: 'Ej: Náuseas, vómito, diarrea, dolor abdominal, alteraciones en apetito...',
        neuropsychiatric: 'Ej: Cefalea, mareos, alteraciones del sueño, ansiedad, depresión...',
        cardio_circulatory: 'Ej: Dolor precordial, palpitaciones, edema, cambios en circulación...',
        urinary: 'Ej: Disuria, poliuria, hematuria, incontinencia...',
        genital_breast: 'Ej: Alteraciones menstruales, secreciones, masas, dolor...',
        respiratory: 'Ej: Disnea, tos, expectoración, dolor torácico...',
        musculoskeletal: 'Ej: Dolor articular, limitación de movimiento, debilidad muscular...'
    };
    return placeholders[name] || 'Describe los síntomas relacionados...';
};


const saveCurrentConditionData = async () => {
    const currentConditionData = {
        current_condition: {
            current_condition: currentCondition.value
        }
    };

    fields.value.forEach(field => {
        currentConditionData.current_condition[field.name] = field.model || '';
    });

    try {
        const response = await api.put(`/pacientes/${props.patientRecord.id}/current-condition`, currentConditionData);
        const updatedAt = new Date(response.data.data.current_condition.updated_at);
        updated_at.value = updatedAt.toString();

        saveToLocalStorage(`patient_${props.patientRecord.id}_current_condition`, currentConditionData.current_condition);
    } catch (error) {
        console.error('Error al guardar los datos del padecimiento actual:', error);
    }
};

// Debounce la función saveCurrentConditionData para que se ejecute solo después de que el usuario deje de escribir
const debouncedSaveCurrentConditionData = debounce(saveCurrentConditionData, 1000);

watch(() => props.data, (newData) => {
    if (newData) {
        currentCondition.value = newData.current_condition || '';
        isCurrentConditionActive.value = currentCondition.value !== '' && currentCondition.value !== null;

        fields.value.forEach(field => {
            const incoming = newData[field.name];
            field.model = typeof incoming === 'string' ? incoming : (incoming ?? '');
            // Solo activar si tiene datos
            field.isActive = field.model !== '' && field.model !== null;
        });

        if (newData.updated_at) {
            updated_at.value = new Date(newData.updated_at).toString();
        }
    }
}, { immediate: true });

</script>

<style scoped>
.current-condition-section-modern {
    --accent: #3b82f6;
    --accent-strong: #2563eb;
    --accent-soft: #eff6ff;
    --border: #f1f5f9;
    --surface: #ffffff;
    --muted-surface: #f8fafc;
    --text: #334155;
    --muted-text: #64748b;
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    padding: 0 1.25rem;
}

.section-header-modern {
    background: transparent;
    border-bottom: 1px solid var(--border);
    padding: 1rem 0;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.header-title-modern {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-title-modern i {
    font-size: 1.25rem;
    color: var(--accent);
    background: var(--accent-soft);
    padding: 0.5rem;
    border-radius: 8px;
}

.header-title-modern h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text);
    margin: 0;
    letter-spacing: -0.025em;
}

.add-fields-section {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    padding: 0.75rem;
    background: #f8f9fa;
    border-radius: 8px;
}

.add-field-btn {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    color: #667eea;
    font-size: 0.813rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.add-field-btn i {
    font-size: 0.75rem;
}

.add-field-btn:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-color: #667eea;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.main-condition-modern,
.field-group-compact {
    background: white;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1.25rem;
    box-shadow: var(--shadow-sm);
    transition: all 0.3s ease;
}

.main-condition-modern:hover,
.field-group-compact:hover {
    box-shadow: var(--shadow);
    border-color: #e2e8f0;
}

.field-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.form-label-modern {
    display: block;
    margin: 0;
}

.label-content-modern {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text);
}

.label-content-modern i {
    color: var(--accent);
}

.close-field-btn {
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 6px;
    transition: all 0.2s;
}

.close-field-btn:hover {
    background: #fee2e2;
    color: #ef4444;
}

.textarea-wrapper-modern {
    position: relative;
}

.form-textarea-modern {
    width: 100%;
    padding: 0.875rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: #f8fafc;
    color: var(--text);
    font-size: 0.95rem;
    line-height: 1.6;
    resize: vertical;
    transition: all 0.2s ease;
}

.form-textarea-modern:focus {
    outline: none;
    background: white;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
}

.form-textarea-modern::placeholder {
    color: #cbd5e1;
}

.textarea-footer-modern {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
}

.textarea-stats-modern {
    font-size: 0.75rem;
    color: #94a3b8;
}

.stat-item-modern {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.systems-section-modern {
    margin-top: 2rem;
}

.subsection-header-modern {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--border);
}

.subsection-header-modern i {
    color: var(--muted-text);
    font-size: 1.1rem;
}

.subsection-header-modern h5 {
    font-size: 1rem;
    font-weight: 600;
    color: var(--muted-text);
    margin: 0;
}

.fields-grid-compact {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.25rem;
}

.field-group-compact.full-width {
    grid-column: 1 / -1;
}

.last-updated-wrapper {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: flex-end;
}
</style>
